import { makeAutoObservable, runInAction } from "mobx";
import { v4 as uuidv4 } from "uuid";
import { BackendError } from "../../../../config/axios";
import { POSTHOG_EVENTS } from "../../../../constants/PostHogEvents";
import { csvFileDownload, fileDownload } from "../../../../helpers/files";
import postHogService from "../../../../services/PostHogService";
import QueryService from "../../../../utils/QueryService";
import { uploadFileToS3 } from "../../../../utils/uploadFileToS3";
import { AsyncProps } from "../../../../utils/useImageUtils";
import { runInChunks } from "../../../Campaign/Brands/utils.tsx";
import { TgModel } from "../../Models/store/tg-model-store";
import { TgProject } from "../../Projects/store/tg-projects-store.tsx";
import { ITgStore } from "../../store/new-tg-stores";

export interface ITgDatasetStore {
	datasetForm: TgDataset;
	formErrors: { upload?: string };
	isFormDuplicateTriggered: boolean;
	advancedDatasetImages: {
		total_count: number;
		sample_training_images: string[];
		sourceZipFile?: string;
	};
	basicSampleTrainingImages: string[];
	fetchVisualSchemaOnMount?: boolean;
	loadingGeneratePrefix: boolean;
	loadingDataset: boolean;
	loadingCreateDataset: boolean;
	loadingUpdateDataset: boolean;
	loadingDeleteDataset: boolean;
	loadingDuplicateDataset: boolean;
	loadingUploadImages: boolean;
	loadingRegenerate: boolean;
	loadingUpdateImage: boolean;
	uploadingCSV: boolean;
	csvUploadErrors: string[];
	csvUploadWarnings: string[];
	loadingDownloadZip: boolean;
	loadingVisualSchema?: boolean;
	loadingRefineVisualSchema?: boolean;

	handleFormChange: <K extends keyof TgDataset>(key: K, value: TgDataset[K]) => void;
	getDataset: (id: number, maxImages?: number) => Promise<TgDataset>;
	createDataset: (datasetToCreate: TgDataset) => Promise<TgDataset>;
	updateDataset: (id: number, updatedDataset: TgDataset) => Promise<TgDataset>;
	deleteDataset: (id: number) => Promise<void>;
	duplicateDataset: (id: number) => Promise<TgDataset>;
	imagesSampleGeneratePrefix: (
		project: TgProject,
		images: { images?: File[]; imagesURLs?: string[] },
	) => Promise<any>;
	uploadImage: (datasetId: number, file: File, imageId?: number) => Promise<TgDatasetImage | void>;
	uploadImages: (datasetId: number, files: File[]) => Promise<void>;
	updateImageCaption: (datasetId: number, imageId: number, newCaption: string) => Promise<TgDatasetImage>;
	deleteImage: (datasetId: number, imageId: number) => Promise<void>;
	regenerateAllCaptions: (
		datasetId: number,
		onComplete?: () => void,
		periodicPollingAction?: ((dataset: TgDataset) => void) | null,
		maxAttempts?: number,
		sleepSecs?: number,
	) => Promise<void>;
	startPollingForCompletion: (
		datasetId: number,
		onComplete?: () => void,
		periodicPollingAction?: ((dataset: TgDataset) => void) | null,
	) => void;
	regenerateImageCaption: (datasetId: number, imageId: number) => Promise<void>;
	downloadDatasetAsCSV: (datasetId: string, fileName: string) => void;

	downloadDatasetZip(datasetId: string, datasetName: string): Promise<void>;

	getAdvancedDatasetDownloadUrls: (datasetId: number) => Promise<{ download_url?: string; captions_url?: string }>;

	uploadCSVFile: (e: React.ChangeEvent<HTMLInputElement>, datasetId: string) => Promise<void>;
	// Simple  presigned upload
	uploadZipSimple: (file: File, onProgress?: (pct: number) => void, fileName?: string) => Promise<string>;
	// Bulk upload
	startBulkImagesUpload: (datasetId: number, imagesZipUrl: string, automaticCaptioning: boolean) => Promise<void>;
	getBulkImagesUploadStatus: (datasetId: number) => Promise<BulkUploadStatus>;
	imagesZipUrl?: string;

	// Captioning / Visual DNA
	getDatasetVisualSchema: (images: string[], useRawImages?: boolean) => Promise<string>;
	refineDatasetDnaSchema: (prompt: string, currentSchema: string, datasetId: number) => Promise<string>;
	calculateAndSetBasicSampleImages: (maxCount: number) => void;

	reset: () => void;
}

export class TgDatasetStore implements ITgDatasetStore {
	private queryService: QueryService = new QueryService("/new-tailored-generation/datasets");
	private imageUploadQueryService: QueryService = new QueryService("/new-tailored-generation/datasets", true);
	tgStore: ITgStore;

	datasetForm: TgDataset = defaultDataset;
	formErrors: { upload?: string } = {};
	advancedDatasetImages: {
		total_count: number;
		sample_training_images: string[];
		sourceZipFile?: string;
	} = {
		total_count: 0,
		sample_training_images: [],
		sourceZipFile: undefined,
	};
	basicSampleTrainingImages: string[] = [];
	fetchVisualSchemaOnMount?: boolean = true;
	isFormDuplicateTriggered: boolean = false;

	loadingGeneratePrefix: boolean = false;
	loadingProjectDatasets: boolean = false;
	loadingDataset: boolean = false;
	loadingCreateDataset: boolean = false;
	loadingUpdateDataset: boolean = false;
	loadingDeleteDataset: boolean = false;
	loadingDuplicateDataset: boolean = false;
	loadingUploadImages: boolean = false;
	loadingRegenerate: boolean = false;
	loadingUpdateImage: boolean = false;
	uploadingCSV: boolean = false;
	csvUploadErrors: string[] = [];
	csvUploadWarnings: string[] = [];
	loadingDownloadZip: boolean = false;
	loadingVisualSchema?: boolean = false;
	loadingRefineVisualSchema?: boolean = false;
	imagesZipUrl?: string;

	constructor(tgStore: ITgStore) {
		makeAutoObservable(this);
		this.tgStore = tgStore;
	}

	handleFormChange = <K extends keyof TgDataset>(key: K, value: TgDataset[K]) => {
		this.datasetForm = { ...this.datasetForm, [key]: value };
	};

	calculateAndSetBasicSampleImages = (maxCount: number = 10) => {
		const imgs = this.datasetForm.images ?? [];
		const limit = Math.min(maxCount, imgs.length);
		if (!limit) {
			runInAction(() => {
				this.basicSampleTrainingImages = [];
			});
			return;
		}
		const indices = new Set<number>();
		while (indices.size < limit) {
			indices.add(Math.floor(Math.random() * imgs.length));
		}
		const urls = [...indices].map((i) => imgs[i].image_url);
		runInAction(() => {
			this.basicSampleTrainingImages = urls;
		});
	};

	getDataset = async (id: number, maxImages?: number): Promise<TgDataset> => {
		try {
			this.loadingDataset = true;
			const dataset = await this.queryService.get(`/${id}`, {
				params: { max_images: typeof maxImages === "number" ? maxImages : undefined },
			});

			runInAction(() => {
				this.loadingDataset = false;
			});

			return dataset;
		} catch (err: any) {
			this.loadingDataset = false;
			return Promise.reject(err);
		}
	};

	createDataset = async (datasetToCreate: TgDataset): Promise<TgDataset> => {
		try {
			this.loadingCreateDataset = true;
			const newDataset = await this.queryService.post(`/`, datasetToCreate);

			runInAction(() => {
				this.loadingCreateDataset = false;
			});

			return newDataset;
		} catch (err: any) {
			this.loadingCreateDataset = false;
			return Promise.reject(err);
		}
	};

	updateDataset = async (id: number, datasetToUpdate: TgDataset): Promise<TgDataset> => {
		try {
			this.loadingUpdateDataset = true;
			const updatedDataset = await this.queryService.put(`/${id}`, datasetToUpdate);

			runInAction(() => {
				this.tgStore.tgProjectStore.paginatedDatasets.items =
					this.tgStore.tgProjectStore.paginatedDatasets?.items.map((dataset) =>
						dataset.id === id ? updatedDataset : dataset,
					);
				this.loadingUpdateDataset = false;
			});
			return updatedDataset;
		} catch (err: any) {
			this.loadingUpdateDataset = false;
			return Promise.reject(err);
		}
	};

	deleteDataset = async (id: number) => {
		try {
			this.loadingDeleteDataset = true;
			await this.queryService.delete(`/${id}`);

			runInAction(() => {
				this.tgStore.tgProjectStore.paginatedDatasets = {
					items: this.tgStore.tgProjectStore.paginatedDatasets.items.filter((dataset) => dataset.id !== id),
					total: this.tgStore.tgProjectStore.paginatedDatasets.total - 1,
				};
				this.loadingDeleteDataset = false;
			});
		} catch (err: any) {
			this.loadingDeleteDataset = false;
			return Promise.reject(err);
		}
	};

	duplicateDataset = async (id: number): Promise<TgDataset> => {
		try {
			this.loadingDuplicateDataset = true;
			const newDataset = await this.queryService.post(`/${id}/duplicate`);

			runInAction(() => {
				this.loadingDuplicateDataset = false;
			});

			return newDataset;
		} catch (err: any) {
			this.loadingDuplicateDataset = false;
			return Promise.reject(err);
		}
	};

	uploadImage = async (dataset_id: number, file: File, imageId?: number): Promise<TgDatasetImage | void> => {
		const updateExistingImage = (updatedImage: TgDatasetImage) => {
			this.datasetForm.images = this.datasetForm.images.map((img) => (img.id === imageId ? updatedImage : img));
		};

		const formData = new FormData();
		formData.append("file", file);

		const skeletonImage: TgDatasetImage = {
			...defaultDatasetImage,
			id: imageId ?? (uuidv4() as unknown as number),
			thumbnail_url: URL.createObjectURL(file),
			caption_source: "automatic",
			file: file,
			uploading: true,
		};

		if (imageId) {
			updateExistingImage(skeletonImage);
		} else {
			this.datasetForm.images = [...this.datasetForm.images, skeletonImage];
		}

		imageId = imageId ?? skeletonImage.id;

		try {
			const newImage: TgDatasetImage = await this.imageUploadQueryService.post(
				`/${dataset_id}/upload_image`,
				formData,
				{
					"Content-Type": "multipart/form-data",
				},
			);

			runInAction(() => {
				updateExistingImage(newImage);
			});

			return { ...newImage, skeleton_id: skeletonImage.id };
		} catch (err: any) {
			updateExistingImage({
				...skeletonImage,
				uploading: false,
				error: {
					message: err.response?.data?.message ?? "Unknown error",
					status: err.response?.status,
				},
			});
		}
	};

	imagesSampleGeneratePrefix = async (project: TgProject, images: { images?: File[]; imagesURLs?: string[] }) => {
		if (!images.images?.length && !images.imagesURLs?.length) return;
		runInAction(() => {
			this.loadingGeneratePrefix = true;
		});
		try {
			const { ip_medium, ip_type, ip_name } = project;
			let prefixResponse = {};
			if (images.images) {
				// Shuffle images to ensure randomness and pick up to 6 images
				images.images = images.images.sort(() => 0.5 - Math.random()).slice(0, 6);
				const base64Images = await Promise.all(
					images.images.map(
						(image) =>
							new Promise<string>((resolve, reject) => {
								const reader = new FileReader();
								reader.readAsDataURL(image);
								reader.onload = () => {
									const base64Data = (reader.result as string).split(",")[1];
									resolve(base64Data);
								};
								reader.onerror = reject;
							}),
					),
				);
				prefixResponse = await this.queryService.post(`/generate_prefix`, {
					ip_medium,
					ip_type,
					ip_name,
					images: base64Images,
				});
			} else if (images.imagesURLs) {
				const imageURLs = images.imagesURLs.sort(() => 0.5 - Math.random()).slice(0, 6);
				prefixResponse = await this.queryService.post(`/generate_prefix`, {
					ip_medium,
					ip_type,
					ip_name,
					image_urls: imageURLs,
				});
			}
			runInAction(() => {
				this.loadingGeneratePrefix = false;
			});
			return prefixResponse;
		} catch (e) {
			runInAction(() => {
				this.loadingGeneratePrefix = true;
			});
			console.error(e);
		}
	};

	uploadImages = async (dataset_id: number, files: File[]): Promise<void> => {
		if (!files.length) return;

		// Calculate dataset size in bytes
		const datasetSize = files.reduce((total, file) => total + file.size, 0);

		this.loadingUploadImages = true;

		try {
			const skeletonImages: TgDatasetImage[] = files.map((file) => ({
				...defaultDatasetImage,
				id: uuidv4() as unknown as number,
				thumbnail_url: URL.createObjectURL(file),
				caption_source: "automatic",
				file,
				uploading: true,
			}));

			this.datasetForm.images = [...this.datasetForm.images, ...skeletonImages];

			const uploadPromises = skeletonImages.map((skeletonImage) => () => {
				if (!skeletonImage.file) return Promise.resolve(); // always return a promise

				try {
					return this.uploadImage(dataset_id, skeletonImage.file, skeletonImage.id);
				} catch (error) {
					console.error(`Failed to upload image ID ${skeletonImage.id}`, error);
					return Promise.resolve(); // swallow error to avoid breaking chunk
				}
			});

			await runInChunks(uploadPromises, 10);

			// Track dataset uploaded
			postHogService.trackEvent(POSTHOG_EVENTS.TG_DATASET_UPLOADED, {
				image_count: files.length,
				dataset_size: datasetSize,
			});
		} catch (error: any) {
			// Track dataset upload failed
			postHogService.trackEvent(POSTHOG_EVENTS.TG_DATASET_UPLOAD_FAILED, {
				error: error?.message || error?.toString() || "Unknown error",
			});

			throw error; // Re-throw to maintain existing error handling
		} finally {
			this.loadingUploadImages = false;
		}
	};

	uploadCSVFile = async (e: React.ChangeEvent<HTMLInputElement>, datasetId: string) => {
		this.loadingUpdateDataset = true;
		this.uploadingCSV = true;
		const file: File | null = e.target.files && e.target.files[0];
		e.target.value = "";
		if (file) {
			this.csvUploadErrors = [];
			this.csvUploadWarnings = [];
			const formData = new FormData();
			formData.append("file", file);
			try {
				const updatedDataset = await this.queryService.post(`/${datasetId}/uploadcsv/`, formData, {
					"Content-Type": "multipart/form-data",
				});
				runInAction(() => {
					if (updatedDataset.issues?.errors) {
						this.csvUploadErrors = updatedDataset.issues.errors;
					}
					if (updatedDataset.issues?.warnings && updatedDataset.issues?.warnings[0]) {
						this.csvUploadWarnings = updatedDataset.issues.warnings;
					}
					if (updatedDataset.dataset?.images) {
						this.datasetForm.images = [...updatedDataset.dataset.images];
					}
				});
			} catch (error: unknown) {
				this.loadingUpdateDataset = false;
				this.uploadingCSV = false;
				this.csvUploadErrors = [];
				this.csvUploadWarnings = [];

				if (error instanceof Error) {
					this.csvUploadErrors = ["Captions update failed: " + error.message];
					throw new Error(this.csvUploadErrors[0]);
				}
			}
		}
		this.loadingUpdateDataset = false;
		this.uploadingCSV = false;
	};

	updateImageCaption = async (datasetId: number, imageId: number, newCaption: string) => {
		try {
			this.loadingUpdateImage = true;
			const updatedImage = await this.queryService.put(`/${datasetId}/images/${imageId}`, {
				caption: newCaption,
			});

			runInAction(() => {
				this.datasetForm.images = this.datasetForm.images.map((img) =>
					img.id === imageId ? updatedImage : img,
				);
				this.loadingUpdateImage = false;
			});

			return updatedImage;
		} catch (err: any) {
			this.loadingUpdateImage = false;
			return Promise.reject(err);
		}
	};

	deleteImage = async (datasetId: number, imageId: number) => {
		try {
			this.loadingUpdateImage = true;

			await this.queryService.delete(`/${datasetId}/images/${imageId}`);

			runInAction(() => {
				this.datasetForm.images = this.datasetForm.images.filter((img) => img.id !== imageId);
				this.loadingUpdateImage = false;
			});
		} catch (err: any) {
			this.loadingUpdateImage = false;
			return Promise.reject(err);
		}
	};

	private pollForCaptionCompletion = async (
		datasetId: number,
		onComplete?: () => void,
		periodicPollingAction: ((dataset: TgDataset) => void) | null = null,
		maxAttempts: number = 110,
		sleepSecs: number = 10,
	) => {
		const pullUntilAvailable = async ({ maxAttempts = 110, sleepSecs = 10 }: AsyncProps = {}) => {
			for (let tries = 0; tries < maxAttempts; tries++) {
				let dataset: TgDataset | null = null;
				try {
					dataset = await this.getDataset(datasetId);
				} catch (err) {
					await new Promise((resolve) => setTimeout(resolve, sleepSecs * 1000));
					continue;
				}
				if (periodicPollingAction) {
					try {
						periodicPollingAction(dataset);
					} catch {
						// ignore error
					}
				}
				if (dataset?.captions_update_status === "completed") {
					return dataset;
				}
				await new Promise((resolve) => setTimeout(resolve, sleepSecs * 1000));
			}
			throw new Error("Caption update did not complete within the maximum attempts");
		};

		pullUntilAvailable({ maxAttempts, sleepSecs })
			.then((newDataset) => {
				runInAction(() => {
					this.datasetForm = newDataset;
				});
				if (onComplete) {
					onComplete();
				}
			})
			.catch((err) => {
				console.error(`Failed to poll for caption completion for dataset ID ${datasetId}:`, err);
			});
	};

	regenerateAllCaptions = async (
		datasetId: number,
		onComplete?: () => void,
		periodicPollingAction: ((dataset: TgDataset) => void) | null = null,
		maxAttempts: number = 110,
		sleepSecs: number = 10,
	) => {
		this.loadingRegenerate = true;

		try {
			await this.queryService.put(`/${datasetId}/images/regenerate`);
			await this.pollForCaptionCompletion(
				datasetId,
				() => {
					if (onComplete) {
						onComplete();
					}
					runInAction(() => {
						this.loadingRegenerate = false;
					});
				},
				periodicPollingAction,
				maxAttempts,
				sleepSecs,
			);
			return Promise.resolve();
		} catch (err: any) {
			this.loadingRegenerate = false;
			return Promise.reject(err);
		}
	};

	startPollingForCompletion = async (
		datasetId: number,
		onComplete?: () => void,
		periodicPollingAction: ((dataset: TgDataset) => void) | null = null,
	) => {
		this.loadingRegenerate = true;
		await this.pollForCaptionCompletion(
			datasetId,
			() => {
				if (onComplete) {
					onComplete();
				}
				runInAction(() => {
					this.loadingRegenerate = false;
				});
			},
			periodicPollingAction,
		);
	};

	regenerateImageCaption = async (datasetId: number, imageId: number) => {
		try {
			this.loadingUpdateImage = true;

			const updatedImage: TgDatasetImage = await this.queryService.put(
				`/${datasetId}/images/${imageId}/regenerate`,
			);

			runInAction(() => {
				this.datasetForm.images = this.datasetForm.images.map((img) =>
					img.id === imageId ? updatedImage : img,
				);
				this.loadingUpdateImage = false;
			});
		} catch (err: any) {
			this.loadingUpdateImage = false;
			return Promise.reject(err);
		}
	};

	downloadDatasetAsCSV = async (datasetId: string, fileName: string) => {
		const csvFile = await this.queryService.get(`/${datasetId}/downloadcsv`);
		csvFileDownload(csvFile, fileName);
	};

	getAdvancedDatasetDownloadUrls = async (
		datasetId: number,
	): Promise<{ download_url?: string; captions_url?: string }> => {
		return await this.queryService.get(`/${datasetId}/advanced/download`);
	};

	uploadZipSimple = async (file: File, onProgress?: (pct: number) => void, fileName?: string): Promise<string> => {
		const final_url = await uploadFileToS3(
			file,
			"zip",
			onProgress,
			fileName ?? (file as any).name ?? "dataset.zip",
		);
		runInAction(() => (this.imagesZipUrl = final_url));
		return final_url;
	};

	startBulkImagesUpload = async (
		datasetId: number,
		imagesZipUrl: string,
		automaticCaptioning: boolean,
	): Promise<void> => {
		await this.queryService.post(`/${datasetId}/images/bulk-upload`, {
			images_zip_url: imagesZipUrl,
			automatic_captioning: automaticCaptioning,
		});
	};

	getBulkImagesUploadStatus = async (datasetId: number): Promise<BulkUploadStatus> => {
		return await this.queryService.get(`/${datasetId}/images/bulk-upload/status`);
	};

	getDatasetVisualSchema = async (images: string[], useRawImages: boolean = false): Promise<string> => {
		try {
			runInAction(() => (this.loadingVisualSchema = true));
			const body = useRawImages ? { images } : { image_urls: images };
			const res = await this.queryService.post(`/visual_schema/generate`, body);
			const visualSchema: string = (res as any)?.visual_schema ?? (typeof res === "string" ? res : "");
			runInAction(() => {
				this.datasetForm.visual_schema = visualSchema;
				this.loadingVisualSchema = false;
			});
			return visualSchema;
		} catch (e) {
			runInAction(() => (this.loadingVisualSchema = false));
			throw e;
		}
	};

	refineDatasetDnaSchema = async (prompt: string, currentSchema: string, datasetId: number): Promise<string> => {
		try {
			runInAction(() => (this.loadingRefineVisualSchema = true));
			const res = await this.queryService.post(`/visual_schema/refine`, {
				instruction: prompt,
				structured_prompt: currentSchema,
				dataset_id: datasetId,
			});
			const visualSchema: string = (res as any)?.structured_prompt ?? (typeof res === "string" ? res : "");
			runInAction(() => {
				this.loadingRefineVisualSchema = false;
			});
			return visualSchema;
		} catch (e) {
			runInAction(() => (this.loadingRefineVisualSchema = false));
			throw e;
		}
	};

	downloadDatasetZip = async (datasetId: string, datasetName: string) => {
		try {
			this.loadingDownloadZip = true;
			const response = await this.queryService.get(`/${datasetId}/downloadzip`, {
				responseType: "blob", // This is important
			});

			fileDownload(URL.createObjectURL(response), `${datasetName}`, "zip");

			runInAction(() => {
				this.loadingDownloadZip = false;
			});
		} catch (err) {
			this.loadingDownloadZip = false;
			return Promise.reject(err);
		}
	};

	reset = () => {
		this.datasetForm = defaultDataset;
		this.formErrors = {};
		this.isFormDuplicateTriggered = false;
		this.loadingGeneratePrefix = false;
		this.loadingDataset = false;
		this.loadingCreateDataset = false;
		this.loadingUpdateDataset = false;
		this.loadingDeleteDataset = false;
		this.loadingDuplicateDataset = false;
		this.loadingUploadImages = false;
		this.loadingRegenerate = false;
		this.loadingUpdateImage = false;
		this.uploadingCSV = false;
		this.csvUploadErrors = [];
		this.csvUploadWarnings = [];
		this.loadingDownloadZip = false;
		this.imagesZipUrl = undefined;
	};
}

export type TgDataset = {
	id: number;
	name: string;
	description: string;
	caption_prefix: string;
	visual_schema?: string;
	captions_update_status?: TgDatasetImageCaptionStatus;
	status: TgDatasetStatus;
	upload_type: TgDatasetUploadType;
	images: TgDatasetImage[];
	images_count?: number;
	models?: TgModel[];
	project_id: number;
	created_at: string;
	updated_at: string;
	captions_source?: TgDatasetCaptionSource;
	training_version?: "fibo";
};

export type TgDatasetStatus = "Draft" | "Completed" | "Deleted" | "Failed";

export type TgDatasetUploadType = "basic" | "advanced";

export type TgDatasetImage = {
	id: number;
	caption: string;
	caption_source: TgDatasetImageCaption;
	image_name: string;
	image_url: string;
	thumbnail_url: string;
	dataset_id: number;
	created_at: string;
	updated_at: string;
	file?: File;
	uploading?: boolean;
	error?: BackendError;
	skeleton_id?: number;
};
export type TgDatasetImageCaption = "automatic" | "manual";
export type TgDatasetImageCaptionStatus = "in_progress" | "completed";
export type TgDatasetCaptionSource = "automatic" | "manual" | "unknown";

export const defaultDataset: TgDataset = {
	id: NaN,
	name: "",
	description: "",
	caption_prefix: "",
	visual_schema: "",
	images: [],
	status: "Draft",
	upload_type: "basic",
	project_id: NaN,
	created_at: "",
	updated_at: "",
};

const defaultDatasetImage: TgDatasetImage = {
	id: NaN,
	caption: "",
	caption_source: "automatic",
	image_name: "",
	image_url: "",
	thumbnail_url: "",
	created_at: "",
	updated_at: "",
	dataset_id: NaN,
};

export type BulkUploadImageStatus = {
	filename: string;
	error_code?: string;
	error_message?: string;
};

export type BulkUploadStatusInfo = "pending" | "processing" | "completed" | "failed";

export type BulkUploadStatus = {
	status?: BulkUploadStatusInfo;
	total_processed: number;
	done: number;
	failed: number;
	progress_percentage?: number;
	error_reason?: string;
	failed_images?: BulkUploadImageStatus[];
};
